/* src/views/logements/NouveauProprio.jsx — LE FORMULAIRE « Nouveau
   propriétaire » et la section « Propriétaires sans logement » de la liste
   du parc (incident 30/09).

   Avant : « ＋ Propriétaire » ajoutait une fiche vide sans rien ouvrir, et la
   liste (qui ne montre que des logements) ne l'affichait nulle part. Quatre
   reclics = quatre fiches vides en base, impossibles à retirer.

   Maintenant :
   · le bouton OUVRE ce formulaire ; rien n'est écrit tant que le nom n'est
     pas rempli ET « Enregistrer » cliqué ; un double clic n'écrit qu'une fois
     (verrou posé AVANT l'attente, ici ET dans le brouillon) ;
   · la fiche créée apparaît aussitôt dans « Propriétaires sans logement »,
     juste au-dessus des boutons, marquée « vient d'être créé » ;
   · un propriétaire sans logement ni historique s'y retire, avec
     confirmation ; sinon la raison est dite, et pas de bouton. */

import { useState, useRef, useEffect } from 'react'
import Feuille from '../accueil/Feuille'
import { propDisplayName } from '../../lib/format'
import { raisonsNonSupprimable } from '../../lib/proprio-creation'

const CHAMP_STYLE = {
  width: '100%', minHeight: '38px', padding: '0 12px', borderRadius: '9px',
  background: '#f8fafc', border: '1px solid #d5e3ee', fontSize: '12.5px',
  color: 'var(--navy-d)', outline: 'none', boxSizing: 'border-box',
}
const LIBELLE_STYLE = {
  display: 'block', fontSize: '11px', color: 'var(--gris)', margin: '9px 0 4px',
}

const SAISIE_VIDE = {
  type: 'particulier', nom_famille: '', prenom: '', nom: '', email: '',
  telephone: '', adresse: '', code_postal: '', ville: '',
}

export function FeuilleNouveauProprio({ onCreer, onFermer, brouillonEnAttente }) {
  const [saisie, setSaisie] = useState(SAISIE_VIDE)
  const [erreur, setErreur] = useState('')
  const [envoi, setEnvoi] = useState(false)
  // Le verrou est une RÉFÉRENCE, pas l'état : deux clics dans la même frame
  // voient tous deux `envoi === false` (l'état n'est relu qu'au rendu
  // suivant) ; la référence, elle, est posée avant la moindre attente.
  const verrou = useRef(false)

  const societe = saisie.type === 'societe'
  const nomRempli = societe ? !!saisie.nom.trim() : !!saisie.nom_famille.trim()

  const poser = (cle) => (e) => {
    const v = e.target.value
    setSaisie((s) => ({ ...s, [cle]: v }))
    setErreur('')
  }

  async function enregistrer() {
    if (verrou.current) return
    if (!nomRempli) {
      setErreur(societe ? 'Le nom de la société est obligatoire.' : 'Le nom de famille est obligatoire.')
      return
    }
    verrou.current = true
    setEnvoi(true)
    try {
      const r = await onCreer(saisie)
      if (r && r.ok) return // l'appelant ferme la feuille et montre la fiche
      if (r && r.enCours) return
      setErreur((r && r.error) || "L'enregistrement a échoué.")
    } catch (e) {
      console.error('[nouveau-proprio] enregistrer', e)
      setErreur("L'enregistrement a échoué.")
    }
    verrou.current = false
    setEnvoi(false)
  }

  const champ = (libelle, cle, type = 'text', placeholder = '') => (
    <label style={{ display: 'block' }}>
      <span style={LIBELLE_STYLE}>{libelle}</span>
      <input type={type} value={saisie[cle]} onChange={poser(cle)}
        data-testid={`nouveau-proprio-${cle}`} placeholder={placeholder}
        style={CHAMP_STYLE} />
    </label>
  )

  return (
    <Feuille titre="Nouveau propriétaire" testId="feuille-nouveau-proprio"
      onFermer={onFermer}
      enfants={(
        <>
          <div className="bascule" style={{ margin: '0 0 4px' }}>
            {[['particulier', 'Un particulier'], ['societe', 'Une société']].map(([id, lb]) => (
              <button key={id} type="button" className={saisie.type === id ? 'on' : ''}
                data-testid={`nouveau-proprio-type-${id}`}
                onClick={() => { setSaisie((s) => ({ ...s, type: id })); setErreur('') }}>{lb}</button>
            ))}
          </div>
          {societe ? champ('Nom de la société (obligatoire)', 'nom') : (
            <>
              {champ('Nom de famille (obligatoire)', 'nom_famille')}
              {champ('Prénom', 'prenom')}
            </>
          )}
          {champ('E-mail', 'email', 'email')}
          {champ('Téléphone', 'telephone', 'tel')}
          {champ('Adresse', 'adresse', 'text', 'nécessaire pour les contrats')}
          {champ('Code postal', 'code_postal')}
          {champ('Ville', 'ville')}
          <p style={{ fontSize: '10.5px', color: 'var(--gris)', lineHeight: '1.5', margin: '10px 0 0' }}>
            Seul le nom est obligatoire ; le reste se complète plus tard.
            Rien n'est enregistré avant « Enregistrer ».</p>
          {brouillonEnAttente && (
            <p data-testid="nouveau-proprio-brouillon" style={{ fontSize: '10.5px', color: '#8a5a17',
              lineHeight: '1.5', margin: '6px 0 0' }}>
              D'autres modifications attendent d'être enregistrées : elles partiront
              en même temps que ce propriétaire.</p>
          )}
          {erreur && (
            <p role="alert" data-testid="nouveau-proprio-erreur"
              style={{ fontSize: '11.5px', color: '#b8412b', margin: '8px 0 0' }}>{erreur}</p>
          )}
          <div className="act">
            <button type="button" className="bt bt-s" onClick={onFermer}
              disabled={envoi}>Annuler</button>
            <button type="button" className="bt bt-p" data-testid="nouveau-proprio-enregistrer"
              disabled={!nomRempli || envoi} onClick={enregistrer}
              style={!nomRempli || envoi ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}>
              {envoi ? 'Enregistrement…' : 'Enregistrer'}</button>
          </div>
        </>
      )} />
  )
}

/* ── les propriétaires qui n'ont encore aucun logement ────────────── */
export function ProprietairesSansLogement({ props, nouveauId, invoicesList, incidents,
  onAjouterLogement, onRetirer }) {
  const [aConfirmer, setAConfirmer] = useState(null)
  const [erreur, setErreur] = useState('')
  const [envoi, setEnvoi] = useState(false)
  const verrou = useRef(false)
  const nouveauRef = useRef(null)

  useEffect(() => {
    if (nouveauId && nouveauRef.current && typeof nouveauRef.current.scrollIntoView === 'function') {
      nouveauRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }
  }, [nouveauId])

  const sansLogement = (props || []).filter((p) => p && !(p.logs || []).length)
  if (!sansLogement.length) return null

  async function retirer(propId) {
    if (verrou.current) return
    verrou.current = true
    setEnvoi(true)
    setErreur('')
    try {
      const r = await onRetirer(propId)
      if (!r || !r.ok) setErreur((r && r.error) || 'Le retrait a échoué.')
      else setAConfirmer(null)
    } catch (e) {
      console.error('[parc] retirer proprio', e)
      setErreur('Le retrait a échoué.')
    }
    verrou.current = false
    setEnvoi(false)
  }

  return (
    <div data-testid="parc-proprios-sans-logement" style={{ margin: '14px 0 4px' }}>
      <p style={{ fontSize: '11px', fontWeight: 600, color: 'var(--gris)',
        textTransform: 'uppercase', letterSpacing: '.04em', margin: '0 0 7px' }}>
        Propriétaires sans logement</p>
      {sansLogement.map((p) => {
        const nom = propDisplayName(p) || p.nom || ''
        const raisons = raisonsNonSupprimable(p, { invoicesList, incidents })
        const estNouveau = p.id === nouveauId
        return (
          <div key={p.id} ref={estNouveau ? nouveauRef : undefined}
            data-testid={`parc-proprio-${p.id}`} className="carte"
            style={{ marginBottom: '8px', padding: '10px 12px',
              ...(estNouveau ? { boxShadow: '0 0 0 2px #87b8e6' } : {}) }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ flex: 1, minWidth: 0, fontSize: '13px', fontWeight: 600,
                color: nom ? 'var(--navy-d)' : 'var(--gris)' }}>
                {nom || 'Propriétaire sans nom'}
                {estNouveau && (
                  <span data-testid="parc-proprio-nouveau" style={{ marginLeft: '7px', fontSize: '9.5px',
                    fontWeight: 600, color: 'var(--vert-tx)', background: 'var(--vert-bg)',
                    borderRadius: '6px', padding: '1px 6px' }}>vient d'être créé</span>
                )}
              </span>
              <span style={{ fontSize: '10.5px', color: 'var(--gris)' }}>aucun logement</span>
            </div>
            <div className="act" style={{ marginTop: '8px' }}>
              {p.id !== 'p6' && (
                <button type="button" className="bt bt-s" data-testid={`parc-proprio-add-log-${p.id}`}
                  onClick={() => onAjouterLogement(p.id)}>＋ Logement</button>
              )}
              {raisons.length === 0 ? (
                aConfirmer === p.id ? (
                  <span style={{ display: 'inline-flex', gap: '7px', alignItems: 'center' }}>
                    <span style={{ fontSize: '11.5px', color: '#8a3b28' }}>Retirer ce propriétaire ?</span>
                    <button type="button" className="bt bt-s" data-testid={`parc-proprio-retirer-oui-${p.id}`}
                      disabled={envoi} style={{ color: '#b8412b', borderColor: '#e8c4b8' }}
                      onClick={() => retirer(p.id)}>Oui, retirer</button>
                    <button type="button" className="bt bt-s" disabled={envoi}
                      onClick={() => { setAConfirmer(null); setErreur('') }}>Non</button>
                  </span>
                ) : (
                  <button type="button" className="bt bt-s" data-testid={`parc-proprio-retirer-${p.id}`}
                    style={{ color: '#b8412b', borderColor: '#e8c4b8' }}
                    onClick={() => { setAConfirmer(p.id); setErreur('') }}>Retirer…</button>
                )
              ) : (
                <span data-testid={`parc-proprio-non-retirable-${p.id}`}
                  style={{ fontSize: '10.5px', color: 'var(--gris)' }}>
                  Ne peut pas être retiré : {raisons.join(', ')}.</span>
              )}
            </div>
            {erreur && aConfirmer === p.id && (
              <p role="alert" style={{ fontSize: '11.5px', color: '#b8412b', margin: '6px 0 0' }}>{erreur}</p>
            )}
          </div>
        )
      })}
    </div>
  )
}
